import { useEffect, useState } from "react";
import { Button } from "@component/button";
import { Badge } from "@component/badge";
import { UserPlusIcon, MoreHorizontalIcon, SearchIcon, UsersIcon } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import AddUserDialog from "@container/add-user-dialog";
import EditUserDialog from "@container/edit-user-dialog";
import WorkspaceReviewersSection from "@container/workspace-reviewers-section";
import { getCompanyPermissions, inviteCompanyPermissions, removeCompanyPermissions } from "@service/user";
import { defaultPagination } from "@constant/app";
import { UserPermissionItem } from "@type/company";
import { EmptyState } from "@component/empty-state";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import LoadingBox from "@component/loading-box";
import { parseFormat } from "@util/date";
import { useUserProfileContext } from "@context/user_profile";
import { useIsOuraMerchant, useOuraMerchantButton, useOuraWorkspace } from "@oura/role";
import { cn } from "@util/tailwind";
import { InviteColleagueDialog } from "@oura/merchant/InviteColleagueDialog";
import { useViewAs } from "@hook/use-view-as";

export default function SettingUserPermissions() {
    const snackbar = useSnackbar();
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    // Oura workspaces are internal-only: two roles (Owner + Admin), no
    // read-only tier and no reviewers. Detection via the trusted hook in
    // @oura/role: JWT role only in production, URL hint only for
    // non-production previews.
    const { isOuraWs: isOura } = useOuraWorkspace();
    // An Oura merchant has one tier (everyone added has full access), so no
    // role column and no role to pick: inviting is the old Team page's
    // "Invite a colleague", and this tab replaces that page.
    const isOuraMerchant = useIsOuraMerchant();
    // Oura's tables label their columns in sentence case, never shouted.
    const thClass = isOura
        ? "text-[13px] font-medium text-gray-500"
        : "text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider";
    // Staff previewing a merchant ("View as") see the list but change nothing:
    // the requests still carry their own login, and these routes would act on
    // the real workspace. The old Team page held the same line.
    const { active: readOnly } = useViewAs();
    const pill = useOuraMerchantButton();
    const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState<boolean>(false);
    const [userPermissions, setUserPermissions] = useState<UserPermissionItem[]>([]);
    const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserPermissionItem | null>(null);

    const onLoad = () => {
        getCompanyPermissions({
            page: defaultPagination.page,
            limit: defaultPagination.limit,
        }).then((response) => {
            if (response.status === 200) {
                setUserPermissions(response.data.data.members);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }

    useEffect(() => {
        onLoad();
    }, [])

    const handleAddUser = () => {
        setIsAddUserDialogOpen(true);
    };

    const handleEditUser = (user: UserPermissionItem) => {
        setSelectedUser(user);
        setIsEditUserDialogOpen(true);
    };

    const handleRemoveUser = async (user: UserPermissionItem) => {
        removeCompanyPermissions({
            user_id: (user.permission === "invited") ? null : user.user_id,
            email: (user.permission === "invited") ? user.email : null,
        }).then((resp) => {
            if (resp.status !== 200) {
                snackbar.showError("Failed to remove member. Please try again later.");
                return;
            }
            onLoad();
            snackbar.showSuccess("User removed successfully!");
        }).catch((error) => {
            snackbar.showError(error.response?.data?.message || "Error removing member.");
            catchErrorLog(`failed to remove member, err: ${error}`);
        })
    };

    const handleResendInvitation = async (user: UserPermissionItem) => {
        try {
            const resp = await inviteCompanyPermissions({
                inviteMember: [{ email: user.email, permission: user.permission }],
            });

            if (resp.status !== 200) {
                snackbar.showError(
                    "Failed to invite member. Please try again later."
                );
                return;
            }
            onLoad();
            snackbar.showSuccess("Invitation has been sent successfully!");
        } catch (error) {
            snackbar.showError("Error resending invitation.");
            catchErrorLog(`failed to resend invitation, err: ${error}`);
        }
    };

    // sorting by permission: owner > write > read > invited
    (userPermissions || []).sort((a: UserPermissionItem, b: UserPermissionItem) => {
        const permissionOrder: Record<string, number> = {
            "owner": 4,
            "write": 3,
            "read": 2,
            "invited": 1,
        };
        return (permissionOrder[b.permission] || 0) - (permissionOrder[a.permission] || 0);
    });

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <h2 className={isOura ? "text-[17px] font-semibold tracking-[-0.01em] text-gray-900" : "text-lg font-medium text-gray-900"}>
                        {isOuraMerchant ? "Who can sign in" : isOura ? "Your team" : "Workspace Members"}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {isOuraMerchant
                            ? "Who can sign in to your Oura account. Everyone you add sees and does everything you can."
                            : isOura
                            ? "Two roles for the internal team: the Owner who activated the workspace, and Admins. Every Admin has the same full access."
                            : "Manage your workspace members and their roles. Members have full access to the platform based on their assigned permissions."}
                    </p>
                </div>
                {!readOnly && currentUserInfo?.permission !== "member" && (
                    <Button
                        onClick={handleAddUser}
                        className={cn("bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2", pill)}
                    >
                        <UserPlusIcon className="h-4 w-4" />
                        {isOuraMerchant ? "Invite colleague" : "Invite Member"}
                    </Button>
                )}

            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                    <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                            <th className={cn("px-4 py-3 text-left", thClass)}>
                                Name
                            </th>
                            <th className={cn("px-4 py-3 text-left", thClass)}>
                                Email
                            </th>
                            <th className={cn("px-4 py-3 text-center", thClass)}>
                                {isOura ? "Joined" : "Date Joined"}
                            </th>
                            {!isOuraMerchant && (
                                <th className={cn("px-4 py-3 text-center", thClass)}>
                                    {isOura ? "Role" : "Workspace Role"}
                                </th>
                            )}
                            <th className={cn("px-4 py-3 text-center", thClass)}>
                                &nbsp;
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {isLoading ? (
                            <tr>
                                <td colSpan={6}>
                                    <LoadingBox />
                                </td>
                            </tr>
                        ) : (
                            <>
                                {userPermissions.length > 0 ? userPermissions.map((user, index) => (
                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {user.first_name} {user.last_name}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                            <span className="truncate cursor-help">{user.email}</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-center text-gray-500 dark:text-gray-400">
                                            {user.joined_at ? `${user.permission === "invited" ? "Invited on" : "Joined on"} ${parseFormat(user.joined_at, "D MMM YYYY")}` : "N/A"}
                                        </td>
                                        {!isOuraMerchant && <td className="px-4 py-3 text-sm text-center">
                                            <Badge variant="secondary"
                                                className={
                                                    user.permission === "owner"
                                                        ? "bg-green-100 text-green-800 hover:bg-green-100 shadow-none capitalize"
                                                        : user.permission === "invited"
                                                            ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 shadow-none capitalize"
                                                            : user.permission === "write"
                                                                ? "bg-blue-100 text-blue-800 hover:bg-blue-100 shadow-none capitalize"
                                                                : user.permission === "read"
                                                                    ? "bg-gray-100 text-gray-800 hover:bg-gray-100 shadow-none capitalize"
                                                                    : "bg-purple-100 text-purple-800 hover:bg-purple-100 shadow-none capitalize"
                                                }
                                                style={{
                                                    borderRadius: "0.75rem",
                                                    paddingTop: "5px",
                                                    paddingBottom: "5px",
                                                }}
                                            >
                                                {user.permission === "owner"
                                                    ? user.user_id === currentUserInfo?.id ? "You (Owner)" : "Owner"
                                                    : user.permission === "invited"
                                                        ? "Invited"
                                                        : user.permission === "write"
                                                            ? user.user_id === currentUserInfo?.id ? "You (Admin)" : "Admin"
                                                            : user.user_id === currentUserInfo?.id ? "You (Member)" : "Member"}
                                            </Badge>
                                        </td>}
                                        <td className="px-4 py-3 text-center">
                                            {!readOnly && ((currentUserInfo?.role === "hr" && currentUserInfo?.permission !== "member") ||
                                              (currentUserInfo?.role === "training-agent" && currentUserInfo?.permission === "owner") ||
                                              (isOura && currentUserInfo?.permission !== "member")) &&
                                              user.permission !== "owner" && user.user_id !== currentUserInfo?.id && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                            <MoreHorizontalIcon className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        {user.permission === "invited" ? (
                                                            <DropdownMenuItem
                                                                onClick={() => handleResendInvitation(user)}
                                                            >
                                                                Resend invitation
                                                            </DropdownMenuItem>
                                                        ) : !isOuraMerchant && (
                                                            <DropdownMenuItem
                                                                onClick={() => handleEditUser(user)}
                                                            >
                                                                Edit workspace role
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem
                                                            className="text-red-600"
                                                            onClick={() => handleRemoveUser(user)}
                                                        >
                                                            Remove from workspace
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                            <EmptyState
                                                icon={isOura ? (
                                                    <UsersIcon className="h-10 w-10 text-gray-300" />
                                                ) : (
                                                    <SearchIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                                                )}
                                                title={isOura ? "Nobody else yet" : "No Members found."}
                                                description={isOura ? (
                                                    readOnly ? "Only you can sign in for now." : "Invite a colleague and they sign in with their own email."
                                                ) : (
                                                    <>
                                                        It seems like you haven't added any members yet.{!readOnly && (<><br />Click the "{isOuraMerchant ? "Invite colleague" : "Invite Member"}" button to get started.</>)}
                                                    </>
                                                )}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Reviewers are a recruiter concept — never shown for Oura's
                internal two-role workspace. */}
            {!isLoadingUserInfo && currentUserInfo?.company_id && !isOura &&
                !["training-agent", "agent"].includes(currentUserInfo?.role || "") && (
                <WorkspaceReviewersSection
                    workspaceId={currentUserInfo.company_id}
                    canManage={currentUserInfo?.permission !== "member"}
                />
            )}

            {!readOnly && (isOuraMerchant ? (
                <InviteColleagueDialog
                    open={isAddUserDialogOpen}
                    onOpenChange={setIsAddUserDialogOpen}
                    existing={userPermissions.map((u) => ({ email: u.email, invited: u.permission === "invited" }))}
                    onSent={onLoad}
                />
            ) : <AddUserDialog
                open={isAddUserDialogOpen}
                onOpenChange={setIsAddUserDialogOpen}
                onLoad={onLoad}
                existingUsers={userPermissions}
            />)}

            {!readOnly && (
                <EditUserDialog
                    open={isEditUserDialogOpen}
                    onOpenChange={setIsEditUserDialogOpen}
                    user={selectedUser}
                    onLoad={onLoad}
                />
            )}
        </div >
    );
}
